import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart3, DollarSign, MousePointer, Eye, Users,
  RefreshCw, AlertCircle, Loader, Sparkles,
  TrendingUp, TrendingDown, ExternalLink, Play, Pause,
  ShoppingCart, Send, ChevronDown, ChevronUp, Bot,
  Plus, X, GripVertical, Target, CheckCircle2, XCircle,
  Phone, Megaphone, DollarSign as Dollar, LogOut, Link2,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

// Traduz o objetivo cru da Meta (ex: "OUTCOME_ENGAGEMENT") pra um selo
// visual em PT-BR, já indicando qual tipo de rastreio de conversão vale
// pra aquela campanha (clique-para-WhatsApp usa ctwa_clid; tráfego usa
// fbclid — ver Pixel de conversão).
function objectiveBadge(objective) {
  const o = (objective || "").toUpperCase();
  if (o.includes("ENGAGEMENT") || o.includes("MESSAGE"))
    return { label: "💬 Mensagens (CTWA)", color: "#a78bfa", bg: "rgba(167,139,250,0.12)" };
  if (o.includes("TRAFFIC"))
    return { label: "🔗 Tráfego", color: "#3b82f6", bg: "rgba(59,130,246,0.12)" };
  if (o.includes("LEAD"))
    return { label: "🎯 Leads", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" };
  if (o.includes("SALE") || o.includes("CONVERSION"))
    return { label: "🛒 Vendas", color: "#22c55e", bg: "rgba(34,197,94,0.12)" };
  if (o.includes("AWARENESS") || o.includes("REACH"))
    return { label: "📢 Alcance", color: "#ec4899", bg: "rgba(236,72,153,0.12)" };
  if (o.includes("APP"))
    return { label: "📱 App", color: "#06b6d4", bg: "rgba(6,182,212,0.12)" };
  return { label: objective || "—", color: "var(--text2)", bg: "transparent" };
}
import { api } from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

// ─── Renderizador simples de Markdown ────────────────────────
function MarkdownText({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.7 }}>
      {lines.map((line, i) => {
        if (line.startsWith("### ")) return <div key={i} style={{ fontWeight: 700, color: "var(--text1)", fontSize: 13, marginTop: 10, marginBottom: 2 }}>{line.slice(4)}</div>;
        if (line.startsWith("## "))  return <div key={i} style={{ fontWeight: 700, color: "var(--text1)", fontSize: 14, marginTop: 12, marginBottom: 4 }}>{line.slice(3)}</div>;
        if (line.startsWith("# "))   return <div key={i} style={{ fontWeight: 700, color: "var(--text1)", fontSize: 15, marginTop: 14, marginBottom: 4 }}>{line.slice(2)}</div>;
        if (line.trim() === "") return <div key={i} style={{ height: 6 }} />;
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <div key={i} style={{ marginBottom: 2 }}>
            {parts.map((part, j) =>
              part.startsWith("**") && part.endsWith("**")
                ? <strong key={j} style={{ color: "var(--text1)", fontWeight: 600 }}>{part.slice(2, -2)}</strong>
                : part
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────
function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n));
}
function fmtBRL(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

// ─── Detecta Capacitor ────────────────────────────────────────
const isNative = () => {
  try { return !!(window?.Capacitor?.isNativePlatform?.()); } catch { return false; }
};

const FB_APP_ID     = "1315261134129802";
const REDIRECT_URI  = "https://automacao-de-sites.onrender.com/api/meta-ads/oauth/callback";
// Não é mais passado na URL (as permissões agora vêm da configuração do
// Login para Empresas, via FB_CONFIG_ID) — mantido só como referência do
// que a config_id acima contém.
const FB_SCOPES     = "ads_read,ads_management,business_management,pages_read_engagement";
// ID da configuração do "Login do Facebook para Empresas" (Meta for Developers
// → Login do Facebook para Empresas → Configurações). Sem isso, o login de
// contas externas (não admin/testador do app) fica bloqueado pela Meta com o
// erro "Login do Facebook está indisponível para ele no momento" — permissões
// de nível empresarial (ads_management, business_management) exigem esse
// fluxo em vez do OAuth de consumidor comum com só `scope`.
const FB_CONFIG_ID  = "2118752412383423";

// ─── OAuth Web ────────────────────────────────────────────────
function openFacebookOAuthWeb() {
  const state = Math.random().toString(36).slice(2);
  sessionStorage.setItem("fb_oauth_state", state);
  const url = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${FB_APP_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&config_id=${FB_CONFIG_ID}&response_type=code&state=${state}`;
  const popup = window.open(url, "fb_oauth", "width=600,height=700,scrollbars=yes");
  return new Promise((resolve, reject) => {
    const timer = setInterval(() => { if (popup?.closed) { clearInterval(timer); reject(new Error("popup_closed")); } }, 500);
    window.addEventListener("message", function handler(e) {
      if (e.data?.type !== "fb_oauth_success") return;
      clearInterval(timer);
      window.removeEventListener("message", handler);
      popup?.close();
      resolve(e.data);
    });
  });
}

// ─── OAuth Nativo ─────────────────────────────────────────────
async function openFacebookOAuthNative() {
  let Browser;
  try { const mod = await import("@capacitor/browser"); Browser = mod.Browser; }
  catch { throw new Error("Plugin @capacitor/browser não instalado."); }
  const state = Math.random().toString(36).slice(2);
  const url = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${FB_APP_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&config_id=${FB_CONFIG_ID}&response_type=code&state=${state}`;
  return new Promise(async (resolve, reject) => {
    const handler = (e) => {
      if (e.detail?.type !== "fb_oauth_success") return;
      window.removeEventListener("fb_oauth_native", handler);
      Browser.close();
      resolve(e.detail);
    };
    window.addEventListener("fb_oauth_native", handler);
    try { await Browser.open({ url, presentationStyle: "popover" }); }
    catch (err) { window.removeEventListener("fb_oauth_native", handler); reject(err); }
    setTimeout(() => { window.removeEventListener("fb_oauth_native", handler); reject(new Error("timeout")); }, 180000);
  });
}

// ─── Seletor de conta ─────────────────────────────────────────
function AdAccountPicker({ accounts, onSelect, loading }) {
  return (
    <div style={{ padding: "0 16px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <Users size={22} color="#25d366" />
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text1)", marginBottom: 6 }}>Qual conta usar?</h2>
        <p style={{ fontSize: 13, color: "var(--text2)" }}>Selecione a conta de anúncios que deseja monitorar.</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {accounts.map(acc => (
          <button key={acc.id} onClick={() => onSelect(acc.id)} disabled={loading} style={{ background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 12, padding: "14px 16px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, opacity: loading ? 0.6 : 1 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text1)", marginBottom: 2 }}>{acc.name}</div>
              <div style={{ fontSize: 11, color: "var(--text2)" }}>{acc.id} · {acc.currency}</div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: acc.account_status === 1 ? "rgba(74,222,128,0.1)" : "var(--bg3)", color: acc.account_status === 1 ? "#4ade80" : "var(--text2)", border: `1px solid ${acc.account_status === 1 ? "rgba(74,222,128,0.3)" : "var(--bg3)"}`, flexShrink: 0 }}>
              {acc.account_status === 1 ? "Ativa" : "Inativa"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Tela de conexão ──────────────────────────────────────────
function ConnectScreen({ onConnect }) {
  const [step, setStep] = useState("idle");
  const [accounts, setAccounts] = useState([]);
  const [oauthToken, setOauthToken] = useState(null);
  const [error, setError] = useState("");

  const handleFacebookLogin = async () => {
    setError(""); setStep("loading");
    try {
      const result = isNative() ? await openFacebookOAuthNative() : await openFacebookOAuthWeb();
      setOauthToken(result.access_token);
      if (result.accounts?.length === 1) await saveConnection(result.access_token, result.accounts[0].id);
      else if (result.accounts?.length > 1) { setAccounts(result.accounts); setStep("pick_account"); }
      else { setError("Nenhuma conta de anúncios encontrada neste perfil."); setStep("idle"); }
    } catch (e) {
      if (!["popup_closed", "timeout"].includes(e?.message)) setError("Erro ao conectar com o Facebook. Tente novamente.");
      setStep("idle");
    }
  };

  const saveConnection = async (token, accountId) => {
    setStep("saving");
    try {
      await api.post("/meta-ads/connect", { access_token: token || oauthToken, ad_account_id: accountId.replace("act_", "") });
      toast.success("Meta Ads conectado! 🎉");
      onConnect();
    } catch (e) { setError(e?.response?.data?.detail || "Erro ao salvar conexão."); setStep("idle"); }
  };

  if (step === "pick_account") return <AdAccountPicker accounts={accounts} loading={step === "saving"} onSelect={(id) => saveConnection(oauthToken, id)} />;

  const isLoading = step === "loading" || step === "saving";
  return (
    <div style={{ padding: "24px 16px" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <BarChart3 size={30} color="#3b82f6" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "var(--text1)", marginBottom: 8 }}>Conectar Meta Ads</h2>
        <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>Autorize com sua conta Facebook em 1 clique — sem precisar copiar tokens.</p>
      </div>
      <div style={{ background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 14, padding: 16, marginBottom: 16 }}>
        {[{ icon: "🔐", text: "Login seguro via Facebook oficial" }, { icon: "⚡", text: "Token de longa duração gerenciado automaticamente" }, { icon: "🤖", text: "IA analisa e otimiza suas campanhas" }].map(({ icon, text }) => (
          <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
            <span style={{ fontSize: 13, color: "var(--text2)" }}>{text}</span>
          </div>
        ))}
      </div>
      {error && (
        <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <AlertCircle size={14} color="#f87171" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: "#f87171", margin: 0 }}>{error}</p>
        </div>
      )}
      <button onClick={handleFacebookLogin} disabled={isLoading} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "14px 16px", background: "#1877F2", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, color: "#fff", cursor: isLoading ? "default" : "pointer", opacity: isLoading ? 0.75 : 1, marginBottom: 12 }}>
        {isLoading ? <Loader size={18} style={{ animation: "spin 0.8s linear infinite" }} /> : <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.532-4.697 1.313 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>}
        {isLoading ? (step === "saving" ? "Salvando..." : "Aguardando autorização...") : "Continuar com Facebook"}
      </button>
      <p style={{ textAlign: "center", fontSize: 11, color: "var(--text2)", lineHeight: 1.5, margin: 0 }}>
        Solicitamos apenas permissão de leitura de anúncios. Você pode revogar o acesso nas configurações do Facebook.
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Card de métrica ──────────────────────────────────────────
function MetricCard({ label, value, sub, icon: Icon, color, trend }) {
  const isPositive = trend > 0;
  return (
    <div style={{
      background: "var(--bg2)",
      border: `1px solid ${color}25`,
      borderRadius: 16,
      padding: "16px 14px",
      flex: 1,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Glow accent top */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent, ${color}80, transparent)`,
      }} />
      {/* Icon + trend */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${color}20`,
          border: `1px solid ${color}40`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 0 12px ${color}20`,
        }}>
          <Icon size={17} color={color} />
        </div>
        {trend !== undefined && (
          <div style={{
            display: "flex", alignItems: "center", gap: 2,
            fontSize: 11, fontWeight: 700,
            color: isPositive ? "#4ade80" : "#f87171",
            background: isPositive ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
            border: `1px solid ${isPositive ? "rgba(74,222,128,0.25)" : "rgba(248,113,113,0.25)"}`,
            borderRadius: 20, padding: "2px 6px",
          }}>
            {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text1)", marginBottom: 2, letterSpacing: "-0.02em" }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: color, opacity: 0.8 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--text2)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Gráfico de evolução diária (gasto x receita x cliques) ───
function EvolutionChart({ data }) {
  if (!data || data.length === 0) return null;

  const chartData = data.map((d) => ({
    ...d,
    dateLabel: (() => {
      const [, m, day] = d.date.split("-");
      return `${day}/${m}`;
    })(),
  }));

  const tooltipFmt = (value, name) => {
    if (name === "Cliques") return [fmt(value), name];
    return [fmtBRL(value), name];
  };

  return (
    <div style={{
      background: "var(--bg2)",
      border: "1px solid #3b82f625",
      borderRadius: 16,
      padding: "16px 14px",
      marginBottom: 14,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: "linear-gradient(90deg, transparent, #3b82f680, transparent)",
      }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <BarChart3 size={15} color="#3b82f6" />
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)" }}>Evolução no período</span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
          <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: "var(--text2)" }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="money" tick={{ fontSize: 11, fill: "var(--text2)" }} axisLine={false} tickLine={false} width={70} />
          <YAxis yAxisId="clicks" orientation="right" tick={{ fontSize: 11, fill: "#f59e0b" }} axisLine={false} tickLine={false} width={40} />
          <Tooltip
            formatter={tooltipFmt}
            contentStyle={{ background: "var(--bg1)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line yAxisId="money" type="monotone" dataKey="spend" name="Gasto" stroke="#3b82f6" strokeWidth={2} dot={false} />
          <Line yAxisId="money" type="monotone" dataKey="revenue" name="Receita" stroke="#22c55e" strokeWidth={2} dot={false} />
          <Line yAxisId="clicks" type="monotone" dataKey="clicks" name="Cliques" stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="4 3" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ToggleSwitch({ checked, onClick, disabled, busy }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      style={{
        position: "relative", width: 40, height: 22, borderRadius: 20, border: "none",
        background: checked ? "#22c55e" : "var(--bg3)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background 0.2s", flexShrink: 0, padding: 0,
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: checked ? 20 : 2,
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)", transition: "left 0.2s",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {busy && <Loader size={10} className="animate-spin" color={checked ? "#22c55e" : "#71717a"} />}
      </span>
    </button>
  );
}

// ─── Linha de campanha (tabela estilo Gerenciador) ────────────
function CampaignRow({ campaign, onCampaignUpdate, onOpenDrawer }) {
  const [actionBusy, setActionBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const isActive = campaign.status === "ACTIVE";
  const ctr = campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) : "0.00";
  const resultsLabel = campaign.purchase_roas != null
    ? `${campaign.purchase_roas.toFixed(2)}x ROAS`
    : campaign.leads > 0
      ? `${fmt(campaign.leads)} leads`
      : `${fmt(campaign.clicks)} cliques`;

  const toggleStatus = async (e) => {
    e.stopPropagation();
    if (actionBusy) return;
    const novoStatus = isActive ? "PAUSED" : "ACTIVE";
    setActionBusy(true);
    try {
      await api.post(`/meta-ads/campaigns/${campaign.id}/status`, { status: novoStatus });
      toast.success(novoStatus === "ACTIVE" ? "Campanha reativada" : "Campanha pausada");
      onCampaignUpdate?.(campaign.id, { status: novoStatus });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 402 || detail === "BILLING_ERROR") {
        toast.error("⚠️ Efetue o pagamento no seu Gerenciador de Anúncios para utilizar esta função.", { duration: 6000 });
      } else if (status === 403 || detail === "PERMISSION_ERROR") {
        toast.error("⚠️ Permissão não autorizada pela Meta. Conclua a análise do app no Meta Developer para liberar esta função.", { duration: 7000 });
      } else {
        toast.error(detail || "Não foi possível alterar o status da campanha.");
      }
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <tr
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onOpenDrawer(campaign)}
      style={{
        background: hover ? "var(--bg3)" : "transparent",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
    >
      {/* Campanha */}
      <td style={{ padding: "12px 14px", maxWidth: 240 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {campaign.name}
        </div>
        {(() => {
          const badge = objectiveBadge(campaign.objective);
          return (
            <span style={{
              display: "inline-block", marginTop: 4, padding: "1px 8px", borderRadius: 20,
              fontSize: 10.5, fontWeight: 700, color: badge.color, background: badge.bg,
              whiteSpace: "nowrap",
            }}>
              {badge.label}
            </span>
          );
        })()}
      </td>

      {/* Status (toggle na linha) */}
      <td style={{ padding: "12px 14px" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ToggleSwitch checked={isActive} onClick={toggleStatus} disabled={actionBusy} busy={actionBusy} />
          <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? "#4ade80" : "var(--text2)" }}>
            {isActive ? "Ativa" : "Pausada"}
          </span>
        </div>
      </td>

      {/* Gasto */}
      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: "var(--text1)", whiteSpace: "nowrap" }}>
        {fmtBRL(campaign.spend)}
      </td>

      {/* Resultados */}
      <td style={{ padding: "12px 14px", fontSize: 12, color: "var(--text2)", whiteSpace: "nowrap" }}>
        {resultsLabel}
      </td>

      {/* CTR */}
      <td style={{ padding: "12px 14px", fontSize: 12, color: "var(--text2)", whiteSpace: "nowrap" }}>
        {ctr}%
      </td>

      {/* Orçamento */}
      <td style={{ padding: "12px 14px", fontSize: 12, color: "var(--text2)", whiteSpace: "nowrap" }}>
        {campaign.daily_budget ? `${fmtBRL(campaign.daily_budget)}/dia` : campaign.lifetime_budget ? `${fmtBRL(campaign.lifetime_budget)} total` : "—"}
      </td>

      {/* Ação (botão IA, aparece no hover) */}
      <td style={{ padding: "12px 14px", textAlign: "right" }}>
        <button
          onClick={(e) => { e.stopPropagation(); onOpenDrawer(campaign); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "6px 12px", borderRadius: 8,
            border: "1px solid rgba(59,130,246,0.3)",
            background: "rgba(59,130,246,0.08)",
            color: "#3b82f6", fontSize: 11.5, fontWeight: 700, cursor: "pointer",
            opacity: hover ? 1 : 0.35, transition: "opacity 0.2s",
          }}
        >
          <Bot size={13} />
          IA
        </button>
      </td>
    </tr>
  );
}

// ─── Painel lateral (drawer) com IA + orçamento ───────────────
function CampaignDrawer({ campaign, onClose, onCampaignUpdate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState(campaign.daily_budget ?? "");
  const chatContainerRef = useRef(null);

  const isActive = campaign.status === "ACTIVE";
  const ctr = campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) : "0.00";

  const toggleStatus = async () => {
    if (actionBusy) return;
    const novoStatus = isActive ? "PAUSED" : "ACTIVE";
    setActionBusy(true);
    try {
      await api.post(`/meta-ads/campaigns/${campaign.id}/status`, { status: novoStatus });
      toast.success(novoStatus === "ACTIVE" ? "Campanha reativada" : "Campanha pausada");
      onCampaignUpdate?.(campaign.id, { status: novoStatus });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 402 || detail === "BILLING_ERROR") {
        toast.error("⚠️ Efetue o pagamento no seu Gerenciador de Anúncios para utilizar esta função.", { duration: 6000 });
      } else if (status === 403 || detail === "PERMISSION_ERROR") {
        toast.error("⚠️ Permissão não autorizada pela Meta. Conclua a análise do app no Meta Developer para liberar esta função.", { duration: 7000 });
      } else {
        toast.error(detail || "Não foi possível alterar o status da campanha.");
      }
    } finally {
      setActionBusy(false);
    }
  };

  const saveBudget = async () => {
    const valor = parseFloat(String(budgetInput).replace(",", "."));
    if (!valor || valor <= 0) {
      toast.error("Informe um orçamento válido.");
      return;
    }
    setActionBusy(true);
    try {
      await api.post(`/meta-ads/campaigns/${campaign.id}/budget`, { daily_budget: valor });
      toast.success("Orçamento diário atualizado");
      onCampaignUpdate?.(campaign.id, { daily_budget: valor });
      setEditingBudget(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 402 || detail === "BILLING_ERROR") {
        toast.error("⚠️ Efetue o pagamento no seu Gerenciador de Anúncios para utilizar esta função.", { duration: 6000 });
      } else if (status === 403 || detail === "PERMISSION_ERROR") {
        toast.error("⚠️ Permissão não autorizada pela Meta. Conclua a análise do app no Meta Developer para liberar esta função.", { duration: 7000 });
      } else {
        toast.error(detail || "Não foi possível atualizar o orçamento.");
      }
    } finally {
      setActionBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitialLoading(true);
      try {
        const { data } = await api.post("/meta-ads/campaign-analyze", { campaign });
        if (!cancelled) setMessages([{ role: "assistant", text: data.analysis }]);
      } catch {
        if (!cancelled) setMessages([{ role: "assistant", text: "Não consegui analisar essa campanha agora. Pode tentar perguntar algo diretamente!" }]);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaign.id]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const newMessages = [...messages, { role: "user", text }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const { data } = await api.post("/meta-ads/campaign-chat", { campaign, messages: newMessages.map(m => ({ role: m.role, content: m.text })) });
      setMessages(prev => [...prev, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Erro ao responder. Tente novamente." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)", animation: "drawerFade 0.2s ease" }} />

      {/* Painel */}
      <div style={{
        position: "relative", width: 440, maxWidth: "92vw", height: "100%",
        background: "hsl(var(--card))", borderLeft: "1px solid hsl(var(--border))",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.5)",
        display: "flex", flexDirection: "column",
        animation: "drawerSlide 0.25s cubic-bezier(0.16,1,0.3,1)",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid hsl(var(--border))", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{campaign.name}</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, marginTop: 6, background: isActive ? "rgba(74,222,128,0.1)" : "var(--bg3)", color: isActive ? "#4ade80" : "var(--text2)", border: `1px solid ${isActive ? "rgba(74,222,128,0.3)" : "var(--bg3)"}` }}>
              {isActive ? <Play size={9} /> : <Pause size={9} />}
              {isActive ? "Ativa" : "Pausada"}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 10, background: "var(--bg3)", border: "1px solid var(--bg3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <X size={16} color="var(--text2)" />
          </button>
        </div>

        {/* Métricas rápidas */}
        <div style={{ padding: "14px 20px", display: "flex", gap: 8, borderBottom: "1px solid hsl(var(--border))" }}>
          {[
            { label: "Gasto", value: fmtBRL(campaign.spend) },
            { label: "Cliques", value: fmt(campaign.clicks) },
            { label: "CTR", value: `${ctr}%` },
            ...(campaign.purchase_roas != null ? [{ label: "ROAS", value: `${campaign.purchase_roas.toFixed(2)}x` }] : campaign.leads > 0 ? [{ label: "Leads", value: fmt(campaign.leads) }] : []),
          ].map(m => (
            <div key={m.label} style={{ flex: 1, textAlign: "center", background: "var(--bg3)", borderRadius: 8, padding: "8px 4px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text1)" }}>{m.value}</div>
              <div style={{ fontSize: 10, color: "var(--text2)" }}>{m.label}</div>
            </div>
          ))}
        </div>

        {/* Ações: pausar/reativar + orçamento */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid hsl(var(--border))" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: editingBudget ? 10 : 0 }}>
            <div
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                padding: "9px 14px", borderRadius: 8,
                border: `1px solid ${isActive ? "rgba(74,222,128,0.3)" : "var(--bg3)"}`,
                background: isActive ? "rgba(74,222,128,0.06)" : "var(--bg3)",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? "#4ade80" : "var(--text2)" }}>
                {isActive ? "Campanha ativa" : "Campanha pausada"}
              </span>
              <ToggleSwitch checked={isActive} onClick={toggleStatus} disabled={actionBusy} busy={actionBusy} />
            </div>
            <button
              onClick={() => setEditingBudget(v => !v)}
              disabled={actionBusy}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                padding: "9px 10px", borderRadius: 8,
                border: "1px solid var(--bg3)", background: "var(--bg3)", color: "var(--text2)",
                fontSize: 12, fontWeight: 600, cursor: actionBusy ? "not-allowed" : "pointer",
                opacity: actionBusy ? 0.6 : 1,
              }}
            >
              <DollarSign size={13} />
              Orçamento
            </button>
          </div>
          {editingBudget && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                type="text"
                inputMode="decimal"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                placeholder="Orçamento diário (R$)"
                style={{ flex: 1, padding: "9px 10px", borderRadius: 8, border: "1px solid var(--bg3)", background: "var(--bg3)", color: "var(--text1)", fontSize: 12, outline: "none" }}
              />
              <button
                onClick={saveBudget}
                disabled={actionBusy}
                style={{ padding: "9px 14px", borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff", fontSize: 12, fontWeight: 600, cursor: actionBusy ? "not-allowed" : "pointer", opacity: actionBusy ? 0.6 : 1 }}
              >
                Salvar
              </button>
            </div>
          )}
        </div>

        {/* Chat IA */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ padding: "12px 20px 8px", display: "flex", alignItems: "center", gap: 6 }}>
            <Bot size={14} color="#3b82f6" />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text1)" }}>Assistente IA</span>
          </div>
          <div ref={chatContainerRef} style={{ flex: 1, overflowY: "auto", padding: "0 20px", display: "flex", flexDirection: "column", gap: 10 }}>
            {initialLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text2)" }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "aiPulse 1.2s ease-in-out infinite" }}>
                  <Sparkles size={13} color="#3b82f6" style={{ animation: "aiSpin 2s linear infinite" }} />
                </div>
                <span style={{ fontSize: 12 }}>Analisando essa campanha...</span>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", flexDirection: msg.role === "user" ? "row-reverse" : "row", gap: 8, alignItems: "flex-start" }}>
                {msg.role === "assistant" && (
                  <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Sparkles size={13} color="#3b82f6" />
                  </div>
                )}
                <div style={{ maxWidth: "82%", padding: "8px 11px", borderRadius: msg.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: msg.role === "user" ? "#3b82f6" : "var(--bg3)", fontSize: 12 }}>
                  {msg.role === "assistant" ? <MarkdownText text={msg.text} /> : <span style={{ color: "#fff" }}>{msg.text}</span>}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "aiPulse 1.2s ease-in-out infinite" }}>
                  <Sparkles size={13} color="#3b82f6" style={{ animation: "aiSpin 2s linear infinite" }} />
                </div>
                <div style={{ background: "var(--bg3)", borderRadius: "12px 12px 12px 2px", padding: "10px 14px", display: "flex", gap: 4, alignItems: "center" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text2)", display: "inline-block", animation: "dot1 1.2s ease-in-out infinite" }} />
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text2)", display: "inline-block", animation: "dot2 1.2s ease-in-out infinite" }} />
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text2)", display: "inline-block", animation: "dot3 1.2s ease-in-out infinite" }} />
                </div>
              </div>
            )}
          </div>
          {messages.length <= 1 && !initialLoading && (
            <div style={{ padding: "10px 20px 6px", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["Como melhorar o CTR?", "Vale a pena escalar?", "Devo pausar?"].map(q => (
                <button key={q} onClick={() => setInput(q)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: "1px solid var(--bg3)", background: "var(--bg3)", color: "var(--text2)", cursor: "pointer" }}>{q}</button>
              ))}
            </div>
          )}
          <div style={{ padding: "8px 20px 20px", display: "flex", gap: 8, alignItems: "flex-end" }}>
            <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder="Pergunte algo sobre essa campanha..." rows={1} style={{ flex: 1, resize: "none", padding: "9px 12px", background: "var(--bg3)", border: "1px solid var(--bg3)", borderRadius: 10, color: "var(--text1)", fontSize: 12, outline: "none", lineHeight: 1.4, fontFamily: "inherit" }} />
            <button onClick={sendMessage} disabled={!input.trim() || loading} style={{ width: 34, height: 34, borderRadius: 10, border: "none", background: input.trim() && !loading ? "#3b82f6" : "var(--bg3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: input.trim() && !loading ? "pointer" : "default", flexShrink: 0, transition: "background 0.2s" }}>
              <Send size={14} color={input.trim() && !loading ? "#fff" : "var(--text2)"} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Constantes do Kanban ─────────────────────────────────────
const KANBAN_COLS = [
  { id: "lead",      label: "Lead",         color: "#3b82f6", icon: "🎯" },
  { id: "contato",   label: "Contato Feito", color: "#f59e0b", icon: "📞" },
  { id: "proposta",  label: "Proposta",      color: "#8b5cf6", icon: "📋" },
  { id: "fechado",   label: "Fechado",       color: "#22c55e", icon: "✅" },
  { id: "perdido",   label: "Perdido",       color: "#ef4444", icon: "❌" },
];

// ─── Modal de novo lead ───────────────────────────────────────
function AddLeadModal({ campaigns, onAdd, onClose }) {
  const [name, setName]         = useState("");
  const [campaign, setCampaign] = useState(campaigns[0]?.name || "");
  const [value, setValue]       = useState("");

  const handleAdd = () => {
    if (!name.trim()) return toast.error("Informe o nome do lead.");
    onAdd({
      id: Date.now().toString(),
      name: name.trim(),
      campaign,
      value: parseFloat(value) || 0,
      col: "lead",
      createdAt: new Date().toISOString(),
    });
    onClose();
  };

  const fieldStyle = {
    width: "100%",
    padding: "12px 14px",
    background: "#1e2235",
    border: "1.5px solid #2e3555",
    borderRadius: 12,
    color: "#f1f5f9",
    fontSize: 15,
    fontWeight: 500,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    letterSpacing: "0.01em",
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: "#94a3b8",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    display: "block",
    marginBottom: 8,
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "flex-end", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%",
          background: "#131929",
          borderRadius: "24px 24px 0 0",
          padding: "24px 20px 44px",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
          border: "1px solid #1e2d45",
          borderBottom: "none",
        }}
      >
        {/* Handle bar */}
        <div style={{ width: 40, height: 4, background: "#2e3555", borderRadius: 4, margin: "0 auto 24px" }} />

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>Novo Lead</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 3, fontWeight: 500 }}>Preencha os dados do lead</div>
          </div>
          <button
            onClick={onClose}
            style={{ width: 36, height: 36, borderRadius: 10, background: "#1e2235", border: "1px solid #2e3555", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <X size={16} color="#94a3b8" />
          </button>
        </div>

        {/* Nome */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>👤 Nome do lead *</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex: João Silva"
            autoFocus
            style={fieldStyle}
          />
        </div>

        {/* Campanha */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>📣 Campanha de origem</label>
          <select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            style={{ ...fieldStyle, appearance: "none", cursor: "pointer" }}
          >
            {campaigns.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            <option value="Manual">Manual (sem campanha)</option>
          </select>
        </div>

        {/* Valor */}
        <div style={{ marginBottom: 28 }}>
          <label style={labelStyle}>💰 Valor estimado (R$)</label>
          <input
            type="number"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="0,00"
            style={fieldStyle}
          />
        </div>

        <button
          onClick={handleAdd}
          style={{
            width: "100%", padding: "15px 0",
            background: "linear-gradient(135deg, #3b82f6, #2563eb)",
            border: "none", borderRadius: 14,
            color: "#fff", fontSize: 16, fontWeight: 800,
            cursor: "pointer", letterSpacing: "0.01em",
            boxShadow: "0 4px 20px rgba(59,130,246,0.4)",
          }}
        >
          Adicionar Lead
        </button>
      </div>
    </div>
  );
}

// ─── Card do lead no Kanban (com drag & touch) ───────────────
function LeadCard({ lead, cols, onMove, onDelete, onDragStart }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  // ── Touch drag (mobile) ──
  const touchRef = useRef(null);
  const handleTouchStart = (e) => {
    touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, moved: false };
  };
  const handleTouchMove = (e) => {
    if (!touchRef.current) return;
    const dx = Math.abs(e.touches[0].clientX - touchRef.current.startX);
    const dy = Math.abs(e.touches[0].clientY - touchRef.current.startY);
    if (dx > 8 || dy > 8) touchRef.current.moved = true;
  };
  const handleTouchEnd = () => { touchRef.current = null; };

  return (
    <div
      draggable
      onDragStart={(e) => { setDragging(true); onDragStart(lead.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => setDragging(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        background: dragging ? "rgba(59,130,246,0.08)" : "var(--bg1)",
        border: `1px solid ${dragging ? "#3b82f6" : "var(--bg3)"}`,
        borderRadius: 10, padding: 12, marginBottom: 8,
        position: "relative", cursor: "grab",
        opacity: dragging ? 0.5 : 1,
        transition: "all 0.15s",
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        {/* Handle de arrastar */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
          <GripVertical size={12} color="var(--text2)" style={{ flexShrink: 0, opacity: 0.5 }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.name}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", color: "var(--text2)", flexShrink: 0 }}
        >
          <ChevronDown size={14} />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
        <Megaphone size={10} color="var(--text2)" />
        <span style={{ fontSize: 11, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.campaign}</span>
      </div>

      {lead.value > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Dollar size={10} color="#22c55e" />
          <span style={{ fontSize: 12, fontWeight: 600, color: "#22c55e" }}>{fmtBRL(lead.value)}</span>
        </div>
      )}

      {/* Menu mover / deletar */}
      {menuOpen && (
        <div style={{ position: "absolute", top: 36, right: 8, zIndex: 20, background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 10, overflow: "hidden", minWidth: 160, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
          <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text2)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>Mover para</div>
          {cols.filter(c => c.id !== lead.col).map(c => (
            <button key={c.id} onClick={() => { onMove(lead.id, c.id); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text1)", fontWeight: 500 }}>
              {c.icon} {c.label}
            </button>
          ))}
          <div style={{ borderTop: "1px solid var(--bg3)" }}>
            <button onClick={() => { onDelete(lead.id); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#ef4444", fontWeight: 500 }}>
              🗑 Remover
            </button>
          </div>
        </div>
      )}
      {menuOpen && <div style={{ position: "fixed", inset: 0, zIndex: 19 }} onClick={() => setMenuOpen(false)} />}
    </div>
  );
}

// ─── Dashboard do Pipeline ────────────────────────────────────
function PipelineDashboard({ leads }) {
  const total       = leads.length;
  const fechados    = leads.filter(l => l.col === "fechado");
  const perdidos    = leads.filter(l => l.col === "perdido");
  const receitaFechada = fechados.reduce((s, l) => s + (l.value || 0), 0);
  const receitaPotencial = leads.filter(l => !["fechado","perdido"].includes(l.col)).reduce((s, l) => s + (l.value || 0), 0);
  const taxaConversao = total > 0 ? ((fechados.length / total) * 100).toFixed(1) : "0.0";

  const cards = [
    { label: "Total de Leads",    value: total,                    icon: Target,       color: "#3b82f6" },
    { label: "Fechados",          value: fechados.length,          icon: CheckCircle2, color: "#22c55e" },
    { label: "Perdidos",          value: perdidos.length,          icon: XCircle,      color: "#ef4444" },
    { label: "Taxa de Conversão", value: `${taxaConversao}%`,      icon: TrendingUp,   color: "#f59e0b" },
  ];

  // Barra por coluna
  const barCols = KANBAN_COLS.map(c => ({
    ...c,
    count: leads.filter(l => l.col === c.id).length,
    pct: total > 0 ? (leads.filter(l => l.col === c.id).length / total) * 100 : 0,
  }));

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Cards de métricas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {cards.map(c => (
          <div key={c.label} style={{
            background: "var(--bg2)",
            border: `1px solid ${c.color}25`,
            borderRadius: 14, padding: "12px 14px",
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: c.color + "80" }} />
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: `${c.color}18`, border: `1px solid ${c.color}35`,
              display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8,
            }}>
              <c.icon size={15} color={c.color} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text1)", letterSpacing: "-0.02em" }}>{c.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.color, opacity: 0.8, marginTop: 1 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Receitas */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{
          flex: 1, background: "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.03))",
          border: "1px solid rgba(34,197,94,0.3)", borderRadius: 14, padding: "12px 14px",
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#22c55e", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
            <span>💰</span> Receita Fechada
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e", letterSpacing: "-0.01em" }}>{fmtBRL(receitaFechada)}</div>
        </div>
        <div style={{
          flex: 1, background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))",
          border: "1px solid rgba(245,158,11,0.3)", borderRadius: 14, padding: "12px 14px",
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
            <span>🎯</span> Potencial
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b", letterSpacing: "-0.01em" }}>{fmtBRL(receitaPotencial)}</div>
        </div>
      </div>

      {/* Barras por estágio */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text1)", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.7 }}>Distribuição por estágio</div>
        {barCols.map(c => (
          <div key={c.id} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: "var(--text2)", display: "flex", alignItems: "center", gap: 5, fontWeight: 500 }}>
                {c.icon} {c.label}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{c.count}</span>
            </div>
            <div style={{ height: 6, background: "var(--bg3)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${c.pct}%`, background: `linear-gradient(90deg, ${c.color}, ${c.color}aa)`, borderRadius: 4, transition: "width 0.5s ease" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Donut de campanha (% meta no centro, anel = ROAS) ────────
function CampaignDonut({ campaign, revenue, goal, compact }) {
  const spend = campaign.spend || 0;
  const roas = spend > 0 ? revenue / spend : 0;
  const goalPct = goal > 0 ? Math.min((revenue / goal) * 100, 999) : 0;

  // Cor do anel pela faixa de ROAS
  const roasColor = roas >= 1.5 ? "#22c55e" : roas >= 1 ? "#f59e0b" : roas > 0 ? "#ef4444" : "var(--bg3)";

  // Preenchimento do anel proporcional ao ROAS (cap em 3x = volta cheia)
  const fillPct = roas > 0 ? Math.min(roas / 3, 1) : 0;
  const size = compact ? 110 : 150;
  const stroke = compact ? 11 : 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * fillPct;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg3)" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={roasColor} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
            style={{ transition: "stroke-dasharray 0.5s ease, stroke 0.3s" }}
          />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: compact ? 22 : 28, fontWeight: 800, color: "var(--text1)", letterSpacing: "-0.02em" }}>
            {goal > 0 ? `${Math.round(goalPct)}%` : "—"}
          </div>
          <div style={{ fontSize: 10, color: "var(--text2)", fontWeight: 600 }}>da meta</div>
        </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: roasColor }}>
          {roas > 0 ? `ROAS ${roas.toFixed(2)}x` : "Sem ROAS"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 1 }}>
          {fmtBRL(revenue)} de {goal > 0 ? fmtBRL(goal) : "—"}
        </div>
      </div>
    </div>
  );
}

// ─── Aba Campanhas reformulada (donuts + meta/venda) ──────────
function CampaignGoalsView({ campaigns, dailySeries, salesData, goalsData, onRefresh, refreshing }) {
  const [saleCampaign, setSaleCampaign] = useState("");
  const [saleValue, setSaleValue] = useState("");
  const [saleNote, setSaleNote] = useState("");
  const [goalCampaign, setGoalCampaign] = useState("");
  const [goalValue, setGoalValue] = useState("");
  const [busy, setBusy] = useState(false);

  const activeCampaigns = campaigns.filter(c => c.status === "ACTIVE");
  const usePillars = activeCampaigns.length > 5;

  const revenueByCamp = salesData?.by_campaign || {};
  const goalByCamp = goalsData?.by_campaign || {};

  const submitSale = async () => {
    if (!saleCampaign) { toast.error("Escolha a campanha da venda."); return; }
    const v = parseFloat(String(saleValue).replace(",", "."));
    if (!v || v <= 0) { toast.error("Informe um valor de venda válido."); return; }
    setBusy(true);
    try {
      const camp = campaigns.find(c => c.id === saleCampaign);
      await api.post("/meta-ads/sales", {
        campaign_id: saleCampaign,
        campaign_name: camp?.name || "",
        value: v,
        note: saleNote,
      });
      toast.success("Venda registrada!");
      setSaleValue(""); setSaleNote("");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar venda.");
    } finally {
      setBusy(false);
    }
  };

  const submitGoal = async () => {
    if (!goalCampaign) { toast.error("Escolha a campanha da meta."); return; }
    const v = parseFloat(String(goalValue).replace(",", "."));
    if (!v || v <= 0) { toast.error("Informe uma meta válida."); return; }
    setBusy(true);
    try {
      await api.post("/meta-ads/goals", { campaign_id: goalCampaign, goal: v });
      toast.success("Meta definida!");
      setGoalValue("");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao definir meta.");
    } finally {
      setBusy(false);
    }
  };

  const deleteSale = async (saleId) => {
    setBusy(true);
    try {
      await api.delete(`/meta-ads/sales/${saleId}`);
      toast.success("Venda removida");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover venda.");
    } finally {
      setBusy(false);
    }
  };

  const editSale = async (sale) => {
    const novo = window.prompt("Novo valor da venda (R$):", String(sale.value));
    if (novo == null) return;
    const v = parseFloat(String(novo).replace(",", "."));
    if (!v || v <= 0) { toast.error("Valor inválido."); return; }
    setBusy(true);
    try {
      await api.put(`/meta-ads/sales/${sale.id}`, { value: v, note: sale.note || "" });
      toast.success("Venda atualizada");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao editar venda.");
    } finally {
      setBusy(false);
    }
  };

  const removeGoal = async (campaignId) => {
    setBusy(true);
    try {
      await api.delete(`/meta-ads/goals/${campaignId}`);
      toast.success("Meta removida");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover meta.");
    } finally {
      setBusy(false);
    }
  };

  const editGoal = async (g) => {
    const novo = window.prompt("Novo valor da meta (R$):", String(g.goal));
    if (novo == null) return;
    const v = parseFloat(String(novo).replace(",", "."));
    if (!v || v <= 0) { toast.error("Valor inválido."); return; }
    setBusy(true);
    try {
      await api.post("/meta-ads/goals", { campaign_id: g.campaign_id, goal: v });
      toast.success("Meta atualizada");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao editar meta.");
    } finally {
      setBusy(false);
    }
  };

  const campName = (cid) => campaigns.find(c => c.id === cid)?.name || "Campanha";

  const inputStyle = {
    width: "100%", padding: "9px 12px", borderRadius: 10,
    border: "1px solid hsl(var(--border))", background: "hsl(var(--card))",
    color: "var(--text1)", fontSize: 13, outline: "none",
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 6, display: "block" };

  return (
    <div>
      {/* Gráfico de evolução (mantido) */}
      <div style={{ position: "relative", borderRadius: 14 }}>
        <EvolutionChart data={dailySeries} />
        {refreshing && <LoadingOverlay />}
      </div>

      {/* Donuts / pilares de ROAS por campanha ativa */}
      <div style={{ marginTop: 18, marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
          <Target size={15} color="#3b82f6" />
          Retorno por campanha ativa ({activeCampaigns.length})
        </div>

        {activeCampaigns.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text2)", background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14 }}>
            <Target size={32} style={{ opacity: 0.15, display: "block", margin: "0 auto 10px" }} />
            <p style={{ fontWeight: 600, color: "var(--text1)" }}>Nenhuma campanha ativa</p>
            <p style={{ fontSize: 13 }}>Ative uma campanha para ver o retorno aqui</p>
          </div>
        ) : usePillars ? (
          // Modo pilares (> 5 campanhas)
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {activeCampaigns.map(c => {
              const revenue = revenueByCamp[c.id] || 0;
              const goal = goalByCamp[c.id] || 0;
              const roas = c.spend > 0 ? revenue / c.spend : 0;
              const roasColor = roas >= 1.5 ? "#22c55e" : roas >= 1 ? "#f59e0b" : roas > 0 ? "#ef4444" : "var(--text2)";
              const goalPct = goal > 0 ? Math.min((revenue / goal) * 100, 100) : 0;
              return (
                <div key={c.id} style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 10 }}>{c.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: roasColor, flexShrink: 0 }}>{roas > 0 ? `${roas.toFixed(2)}x` : "—"}</div>
                  </div>
                  <div style={{ height: 8, background: "var(--bg3)", borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
                    <div style={{ height: "100%", width: `${goalPct}%`, background: roasColor, borderRadius: 4, transition: "width 0.5s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text2)" }}>
                    <span>Gasto: {fmtBRL(c.spend)} · Receita: {fmtBRL(revenue)}</span>
                    <span>{goal > 0 ? `${Math.round(goalPct)}% da meta` : "sem meta"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Modo donuts (≤ 5 campanhas)
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
            {activeCampaigns.map(c => (
              <div key={c.id} style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: "16px 12px" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text1)", textAlign: "center", marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                <CampaignDonut campaign={c} revenue={revenueByCamp[c.id] || 0} goal={goalByCamp[c.id] || 0} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulários: definir meta + registrar venda */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* Definir meta */}
        <div style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Target size={14} color="#8b5cf6" /> Definir meta de receita
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Campanha</label>
            <select className="meta-select" value={goalCampaign} onChange={(e) => setGoalCampaign(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">Selecione...</option>
              {activeCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Meta de receita (R$)</label>
            <input type="text" inputMode="decimal" value={goalValue} onChange={(e) => setGoalValue(e.target.value)} placeholder="Ex: 1000" style={inputStyle} />
          </div>
          <button onClick={submitGoal} disabled={busy} style={{ width: "100%", padding: "9px", borderRadius: 10, border: "none", background: "#8b5cf6", color: "#fff", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
            Salvar meta
          </button>
        </div>

        {/* Registrar venda */}
        <div style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <DollarSign size={14} color="#22c55e" /> Registrar venda fechada
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Campanha que gerou</label>
            <select className="meta-select" value={saleCampaign} onChange={(e) => setSaleCampaign(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">Selecione...</option>
              {activeCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Valor (R$)</label>
              <input type="text" inputMode="decimal" value={saleValue} onChange={(e) => setSaleValue(e.target.value)} placeholder="Ex: 150" style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Obs (opcional)</label>
              <input type="text" value={saleNote} onChange={(e) => setSaleNote(e.target.value)} placeholder="Cliente..." style={inputStyle} />
            </div>
          </div>
          <button onClick={submitSale} disabled={busy} style={{ width: "100%", padding: "9px", borderRadius: 10, border: "none", background: "#22c55e", color: "#fff", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
            Registrar venda
          </button>
        </div>
      </div>

      {/* Listas de metas e vendas registradas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
        {/* Metas definidas */}
        <div style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", marginBottom: 12 }}>Metas definidas</div>
          {(goalsData?.goals || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text2)", textAlign: "center", padding: "16px 0" }}>Nenhuma meta definida</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(goalsData.goals).map(g => (
                <div key={g.campaign_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", background: "var(--bg3)", borderRadius: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{campName(g.campaign_id)}</div>
                    <div style={{ fontSize: 11, color: "var(--text2)" }}>{fmtBRL(g.goal)}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => editGoal(g)} disabled={busy} title="Editar" style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid hsl(var(--border))", background: "transparent", color: "var(--text2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <DollarSign size={13} />
                    </button>
                    <button onClick={() => removeGoal(g.campaign_id)} disabled={busy} title="Remover" style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.06)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vendas registradas */}
        <div style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", marginBottom: 12 }}>Vendas registradas</div>
          {(salesData?.sales || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text2)", textAlign: "center", padding: "16px 0" }}>Nenhuma venda registrada</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 260, overflowY: "auto" }}>
              {(salesData.sales).map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", background: "var(--bg3)", borderRadius: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fmtBRL(s.value)} {s.note ? `· ${s.note}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.campaign_name || campName(s.campaign_id)}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => editSale(s)} disabled={busy} title="Editar valor" style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid hsl(var(--border))", background: "transparent", color: "var(--text2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <DollarSign size={13} />
                    </button>
                    <button onClick={() => deleteSale(s.id)} disabled={busy} title="Excluir" style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.06)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <UtmSummarySection />
    </div>
  );
}

// ─── Resumo de links UTM (cliques + vendas atribuídas) ─────────
function UtmSummarySection() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/utm/links")
      .then(({ data }) => setLinks(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (links.length === 0) {
    return (
      <div style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
          <Link2 size={15} color="#3b82f6" /> Links UTM
        </div>
        <p style={{ fontSize: 12, color: "var(--text2)" }}>
          Você ainda não criou nenhum link rastreável. Crie um na página <strong>UTMs</strong> do menu lateral para
          acompanhar cliques e vendas por campanha/plataforma aqui.
        </p>
      </div>
    );
  }

  const sorted = [...links].sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
  const totalClicks = links.reduce((s, l) => s + (l.clicks || 0), 0);
  const totalRevenue = links.reduce((s, l) => s + (l.revenue || 0), 0);

  return (
    <div style={{ background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)", display: "flex", alignItems: "center", gap: 6 }}>
          <Link2 size={15} color="#3b82f6" /> Links UTM ({links.length})
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text2)" }}>
          {totalClicks} cliques · {fmtBRL(totalRevenue)} em vendas
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 260, overflowY: "auto" }}>
        {sorted.map(link => (
          <div key={link.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", background: "var(--bg3)", borderRadius: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {link.label}
              </div>
              <div style={{ fontSize: 11, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {link.platform} {link.utm_source ? `· ${link.utm_source}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 14, flexShrink: 0, fontSize: 12 }}>
              <span style={{ color: "var(--text1)", fontWeight: 700 }}>{link.clicks} <span style={{ color: "var(--text2)", fontWeight: 400 }}>cliques</span></span>
              {link.revenue > 0 && (
                <span style={{ color: "#22c55e", fontWeight: 700 }}>{fmtBRL(link.revenue)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 5,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
      background: "var(--bg2)", opacity: 0.85, borderRadius: "inherit",
    }}>
      <Loader size={22} className="animate-spin" color="#3b82f6" />
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)" }}>Atualizando dados do período...</span>
    </div>
  );
}

function DashboardStatusToggle({ campaign, onCampaignUpdate }) {
  const [busy, setBusy] = useState(false);
  const isActive = campaign.status === "ACTIVE";

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    const novoStatus = isActive ? "PAUSED" : "ACTIVE";
    setBusy(true);
    try {
      await api.post(`/meta-ads/campaigns/${campaign.id}/status`, { status: novoStatus });
      toast.success(novoStatus === "ACTIVE" ? "Campanha reativada" : "Campanha pausada");
      onCampaignUpdate?.(campaign.id, { status: novoStatus });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 402 || detail === "BILLING_ERROR") {
        toast.error("⚠️ Efetue o pagamento no seu Gerenciador de Anúncios para utilizar esta função.", { duration: 6000 });
      } else if (status === 403 || detail === "PERMISSION_ERROR") {
        toast.error("⚠️ Permissão não autorizada pela Meta. Conclua a análise do app no Meta Developer para liberar esta função.", { duration: 7000 });
      } else {
        toast.error(detail || "Não foi possível alterar o status da campanha.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={e => e.stopPropagation()}>
      <ToggleSwitch checked={isActive} onClick={toggle} disabled={busy} busy={busy} />
      <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? "#4ade80" : "var(--text2)" }}>
        {isActive ? "Ativa" : "Pausada"}
      </span>
    </div>
  );
}

// ─── Aba Dashboard (estilo UTMify) ────────────────────────────
function DashboardTab({ campaigns, dateRange, setDateRange, onChangeRange, onOpenDrawer, onCampaignUpdate, refreshing }) {
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const filtered = campaigns.filter(c => {
    const matchName = c.name.toLowerCase().includes(nameFilter.toLowerCase());
    const matchStatus =
      statusFilter === "all" ? true :
      statusFilter === "active" ? c.status === "ACTIVE" :
      c.status !== "ACTIVE";
    return matchName && matchStatus;
  });

  const allSelected = filtered.length > 0 && filtered.every(c => selected.has(c.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(c => c.id)));
    }
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Totais consolidados das campanhas filtradas
  const totSpend = filtered.reduce((s, c) => s + (c.spend || 0), 0);
  const totConv = filtered.reduce((s, c) => s + (c.conversions || 0), 0);
  const totRevenue = filtered.reduce((s, c) => s + (c.pixel_revenue || 0), 0);
  const allTracked = filtered.length > 0 && filtered.some(c => c.conversions > 0);

  const selectStyle = {
    width: "100%", padding: "9px 12px", borderRadius: 10,
    border: "1px solid hsl(var(--border))", background: "hsl(var(--card))",
    color: "var(--text1)", fontSize: 13, outline: "none", cursor: "pointer",
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 6, display: "block" };

  return (
    <div>
      {/* Barra de status */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", borderRadius: 20,
          background: allTracked ? "rgba(34,197,94,0.1)" : "var(--bg2)",
          border: `1px solid ${allTracked ? "rgba(34,197,94,0.3)" : "hsl(var(--border))"}`,
          color: allTracked ? "#22c55e" : "var(--text2)",
          fontSize: 12, fontWeight: 600,
        }}>
          <CheckCircle2 size={13} />
          {allTracked ? "Vendas trackeadas" : "Sem conversões no período"}
        </div>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--text2)" }}>
          {filtered.length} campanha{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 12, marginBottom: 18,
        background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16,
      }}>
        <div>
          <label style={labelStyle}>Nome da Campanha</label>
          <input
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Filtrar por nome..."
            style={{ ...selectStyle, cursor: "text" }}
          />
        </div>
        <div>
          <label style={labelStyle}>Status da Campanha</label>
          <select className="meta-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="all">Qualquer</option>
            <option value="active">Ativas</option>
            <option value="paused">Pausadas</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Período de Visualização</label>
          <select className="meta-select" value={dateRange} onChange={(e) => onChangeRange(e.target.value)} style={selectStyle}>
            <option value="today">Hoje</option>
            <option value="yesterday">Ontem</option>
            <option value="last_7d">Últimos 7 dias</option>
            <option value="last_30d">Últimos 30 dias</option>
            <option value="last_90d">Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {/* Resumo */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
        {[
          { label: "Gasto total", value: fmtBRL(totSpend) },
          { label: "Conversões", value: fmt(totConv) },
          { label: "Receita (Pixel)", value: totRevenue > 0 ? fmtBRL(totRevenue) : "—" },
          { label: "Custo/Conversão", value: totConv > 0 ? fmtBRL(totSpend / totConv) : "—" },
        ].map(m => (
          <div key={m.label} style={{ flex: 1, background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text1)", letterSpacing: "-0.02em" }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Tabela estilo UTMify */}
      <div style={{ position: "relative", background: "var(--bg2)", border: "1px solid hsl(var(--border))", borderRadius: 14, overflow: "hidden" }}>
        {refreshing && <LoadingOverlay />}
        <table className="utmify-grid" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ width: 44, padding: "12px 14px", textAlign: "center" }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor: "pointer", width: 15, height: 15 }} />
              </th>
              {["Status", "Campanha", "Mensagens/Cliques", "Conversões", "Custo/Conversão", "Orçamento"].map((h, i) => (
                <th key={i} style={{
                  padding: "12px 14px", textAlign: "left",
                  fontSize: 11, fontWeight: 700, color: "var(--text2)",
                  textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const isActive = c.status === "ACTIVE";
              const isSel = selected.has(c.id);
              return (
                <tr
                  key={c.id}
                  onClick={() => onOpenDrawer(c)}
                  className="utmify-row"
                  style={{ cursor: "pointer", background: isSel ? "rgba(59,130,246,0.06)" : "transparent" }}
                >
                  <td style={{ padding: "12px 14px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleOne(c.id)} style={{ cursor: "pointer", width: 15, height: 15 }} />
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <DashboardStatusToggle campaign={c} onCampaignUpdate={onCampaignUpdate} />
                  </td>
                  <td style={{ padding: "12px 14px", maxWidth: 260 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2 }}>{fmtBRL(c.spend)} gasto</div>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, whiteSpace: "nowrap" }}>
                    {(() => {
                      const badge = objectiveBadge(c.objective);
                      const isMsg = badge.label.includes("Mensagens");
                      const value = isMsg ? c.messages_started : c.clicks;
                      return (
                        <span style={{ color: value > 0 ? "var(--text1)" : "var(--text2)", fontWeight: value > 0 ? 700 : 400 }}>
                          {fmt(value || 0)} <span style={{ fontSize: 11, color: "var(--text2)", fontWeight: 400 }}>{isMsg ? "msgs" : "cliques"}</span>
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: c.conversions > 0 ? "var(--text1)" : "var(--text2)" }}>
                    {c.conversions > 0 ? fmt(c.conversions) : "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text2)" }}>
                    {c.cost_per_conversion != null ? fmtBRL(c.cost_per_conversion) : "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text2)", whiteSpace: "nowrap" }}>
                    {c.daily_budget ? `${fmtBRL(c.daily_budget)}/dia` : c.lifetime_budget ? `${fmtBRL(c.lifetime_budget)} total` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text2)" }}>
            <BarChart3 size={36} style={{ opacity: 0.15, display: "block", margin: "0 auto 10px" }} />
            <p style={{ fontWeight: 600, color: "var(--text1)" }}>Nenhuma campanha encontrada</p>
            <p style={{ fontSize: 13 }}>Ajuste os filtros acima</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Aba Pipeline (Kanban + Dashboard) ───────────────────────
function PipelineTab({ campaigns }) {
  const STORAGE_KEY = "meta_pipeline_leads";
  const [leads, setLeads] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
  });
  const [showModal, setShowModal]   = useState(false);
  const [draggingId, setDraggingId] = useState(null); // id do card sendo arrastado
  const [overCol, setOverCol]       = useState(null);  // coluna em destaque durante drag

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));
  }, [leads]);

  const importMetaLeads = () => {
    const existing = new Set(leads.map(l => l.campaign + l.name));
    const newLeads = [];
    campaigns.forEach(c => {
      if ((c.leads || 0) > 0) {
        const key = c.name + `Lead via ${c.name}`;
        if (!existing.has(key)) {
          newLeads.push({
            id: `meta_${c.id}_${Date.now()}`,
            name: `Lead via ${c.name}`,
            campaign: c.name,
            value: c.spend > 0 ? parseFloat((c.spend / c.leads).toFixed(2)) : 0,
            col: "lead",
            createdAt: new Date().toISOString(),
          });
        }
      }
    });
    if (newLeads.length === 0) return toast.info("Nenhum lead novo para importar.");
    setLeads(prev => [...prev, ...newLeads]);
    toast.success(`${newLeads.length} lead(s) importado(s)! 🎉`);
  };

  const addLead    = (lead) => setLeads(prev => [lead, ...prev]);
  const moveLead   = (id, col) => setLeads(prev => prev.map(l => l.id === id ? { ...l, col } : l));
  const deleteLead = (id) => setLeads(prev => prev.filter(l => l.id !== id));

  // ── Handlers de drag & drop ──
  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverCol(colId);
  };
  const handleDrop = (e, colId) => {
    e.preventDefault();
    if (draggingId) moveLead(draggingId, colId);
    setDraggingId(null);
    setOverCol(null);
  };
  const handleDragLeave = () => setOverCol(null);

  return (
    <div>
      <PipelineDashboard leads={leads} />

      {/* Botões de ação */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button onClick={() => setShowModal(true)} style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          padding: "13px 12px",
          background: "linear-gradient(135deg, #3b82f6, #2563eb)",
          border: "none", borderRadius: 12,
          color: "#fff", fontSize: 14, fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 4px 16px rgba(59,130,246,0.4)",
          letterSpacing: "0.01em",
        }}>
          <Plus size={16} /> Novo Lead
        </button>
        <button onClick={importMetaLeads} style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          padding: "13px 12px",
          background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(139,92,246,0.08))",
          border: "1px solid rgba(139,92,246,0.4)",
          borderRadius: 12,
          color: "#a78bfa", fontSize: 14, fontWeight: 700,
          cursor: "pointer",
          letterSpacing: "0.01em",
        }}>
          <Sparkles size={16} /> Importar do Meta
        </button>
      </div>

      {/* Dica de uso */}
      {leads.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, padding: "8px 12px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 8 }}>
          <GripVertical size={12} color="#3b82f6" />
          <span style={{ fontSize: 11, color: "#3b82f6", fontWeight: 500 }}>Arraste os cards entre as colunas ou use o menu ˅</span>
        </div>
      )}

      {/* Kanban — scroll horizontal */}
      <div style={{ overflowX: "auto", marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
        <div style={{ display: "flex", gap: 10, minWidth: `${KANBAN_COLS.length * 220}px` }}>
          {KANBAN_COLS.map(col => {
            const colLeads = leads.filter(l => l.col === col.id);
            const isOver = overCol === col.id;
            return (
              <div
                key={col.id}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDrop={(e) => handleDrop(e, col.id)}
                onDragLeave={handleDragLeave}
                style={{
                  flex: "0 0 210px",
                  background: isOver ? `${col.color}12` : "var(--bg2)",
                  border: `1.5px solid ${isOver ? col.color : col.color + "30"}`,
                  borderRadius: 14, padding: 12,
                  transition: "all 0.15s",
                  minHeight: 140,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Top accent */}
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg, ${col.color}cc, ${col.color}44)`,
                }} />
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, marginTop: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 15 }}>{col.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: col.color }}>{col.label}</span>
                  </div>
                  <div style={{
                    fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
                    background: `${col.color}20`, color: col.color,
                    border: `1px solid ${col.color}40`,
                    minWidth: 22, textAlign: "center",
                  }}>
                    {colLeads.length}
                  </div>
                </div>

                {/* Zona de drop vazia */}
                {colLeads.length === 0 ? (
                  <div style={{
                    textAlign: "center", padding: "24px 8px",
                    color: isOver ? col.color : "var(--text2)",
                    fontSize: 12, opacity: isOver ? 1 : 0.4,
                    border: `2px dashed ${isOver ? col.color : col.color + "40"}`,
                    borderRadius: 10, transition: "all 0.15s",
                    margin: "4px 0",
                  }}>
                    {isOver ? "Solte aqui!" : "Nenhum lead"}
                  </div>
                ) : (
                  colLeads.map(lead => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      cols={KANBAN_COLS}
                      onMove={moveLead}
                      onDelete={deleteLead}
                      onDragStart={setDraggingId}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && <AddLeadModal campaigns={campaigns} onAdd={addLead} onClose={() => setShowModal(false)} />}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────
export default function MetaAdsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAgencyOrAdmin =
    user?.is_admin || user?.plan_key === "agency";

  if (!isAgencyOrAdmin) {
    return (
      <div className="p-6 md:p-8 max-w-[760px] mx-auto">
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center mx-auto mb-5">
            <BarChart3 className="h-7 w-7 text-blue-600" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight mb-2">
            Análise de Meta Ads
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-md mx-auto mb-6">
            Esse recurso está disponível a partir do plano <strong>Agência</strong>. Conecte sua conta
            do Meta Ads, acompanhe métricas das campanhas e receba análises com inteligência artificial
            pra otimizar seus anúncios.
          </p>
          <button
            onClick={() => navigate("/planos")}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 h-11 font-medium transition-colors"
          >
            <Sparkles className="h-4 w-4" /> Ver planos e fazer upgrade
          </button>
        </div>
      </div>
    );
  }

  return <MetaAdsContent />;
}

function MetaAdsContent() {
  const [connected, setConnected]   = useState(false);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData]             = useState(null);
  const [aiLoading, setAiLoading]   = useState(false);
  const [analysis, setAnalysis]     = useState(null);
  const [dateRange, setDateRange]   = useState("last_7d");
  const [activeTab, setActiveTab]   = useState("campanhas"); // "campanhas" | "pipeline"
  const [drawerCampaign, setDrawerCampaign] = useState(null);
  const [salesData, setSalesData]   = useState({ by_campaign: {}, total_revenue: 0, sales: [] });
  const [goalsData, setGoalsData]   = useState({ by_campaign: {}, goals: [] });
  const [pixelId, setPixelId]       = useState("");
  const [pixelInput, setPixelInput] = useState("");
  const [pageId, setPageId]         = useState("");
  const [pageInput, setPageInput]   = useState("");
  const [showPixelModal, setShowPixelModal] = useState(false);
  const [pixelSaving, setPixelSaving] = useState(false);
  const [datasetOptions, setDatasetOptions] = useState([]);
  const [pageOptions, setPageOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const DATE_RANGES = [
    { value: "today",     label: "Hoje" },
    { value: "yesterday", label: "Ontem" },
    { value: "last_7d",  label: "7d" },
    { value: "last_30d", label: "30d" },
    { value: "last_90d", label: "90d" },
  ];

  const fetchData = useCallback(async (range = dateRange) => {
    setRefreshing(true);
    try {
      const { data: res } = await api.get("/meta-ads/data", { params: { range } });
      setData(res);
      setConnected(true);
    } catch (e) {
      if (e?.response?.status === 404) setConnected(false);
      else if (e?.response?.status === 402 || e?.response?.data?.detail === "BILLING_ERROR")
        toast.error("⚠️ Efetue o pagamento no seu Gerenciador de Anúncios para utilizar o Meta Ads.", { duration: 6000 });
      else toast.error("Erro ao carregar dados");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [dateRange]);

  const fetchSalesAndGoals = useCallback(async () => {
    try {
      const [salesRes, goalsRes] = await Promise.all([
        api.get("/meta-ads/sales"),
        api.get("/meta-ads/goals"),
      ]);
      setSalesData(salesRes.data || { by_campaign: {}, total_revenue: 0, sales: [] });
      setGoalsData(goalsRes.data || { by_campaign: {}, goals: [] });
    } catch {
      // silencioso: vendas/metas são opcionais
    }
  }, []);

  const fetchAI = useCallback(async () => {
    setAiLoading(true);
    setAnalysis(null);
    try {
      const { data: res } = await api.post("/meta-ads/analyze");
      setAnalysis(res.analysis);
    } catch {
      toast.error("Erro ao gerar análise");
    } finally {
      setAiLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); fetchSalesAndGoals(); }, []);
  useEffect(() => { if (data && !analysis) fetchAI(); }, [data]);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get("/meta-ads/pixel");
        setPixelId(res.pixel_id || "");
        setPixelInput(res.pixel_id || "");
        setPageId(res.page_id || "");
        setPageInput(res.page_id || "");
      } catch { /* silencioso */ }
    })();
  }, []);

  const loadMetaOptions = async () => {
    setLoadingOptions(true);
    try {
      const [dsRes, pgRes] = await Promise.all([
        api.get("/meta-ads/datasets"),
        api.get("/meta-ads/pages"),
      ]);
      setDatasetOptions(dsRes.data?.datasets || []);
      setPageOptions(pgRes.data?.pages || []);
    } catch {
      toast.error("Não consegui listar seus Datasets/Páginas automaticamente. Pode colar o ID manualmente.");
    } finally { setLoadingOptions(false); }
  };

  const handleSavePixel = async () => {
    setPixelSaving(true);
    try {
      const { data: res } = await api.post("/meta-ads/pixel-config", { pixel_id: pixelInput.trim(), page_id: pageInput.trim() });
      setPixelId(res.pixel_id);
      setPageId(res.page_id);
      setShowPixelModal(false);
      toast.success("🎯 Pixel salvo! Agora conversões de campanhas clique-para-WhatsApp são reportadas de volta pra Meta.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar o Pixel ID.");
    } finally { setPixelSaving(false); }
  };

  const handleDisconnect = async () => {
    try {
      await api.post("/meta-ads/disconnect");
      setConnected(false);
      setData(null);
      setAnalysis(null);
    } catch {}
  };

  if (loading) return (
    <div style={{
      display: "flex", flexDirection: "column",
      justifyContent: "center", alignItems: "center",
      height: "100%", padding: 80, gap: 20,
    }}>
      {/* Anel giratório */}
      <div style={{ position: "relative", width: 56, height: 56 }}>
        <svg width="56" height="56" viewBox="0 0 56 56" style={{ position: "absolute", inset: 0 }}>
          {/* Trilha */}
          <circle cx="28" cy="28" r="22" fill="none" stroke="var(--bg3)" strokeWidth="4" />
          {/* Arco animado */}
          <circle
            cx="28" cy="28" r="22" fill="none"
            stroke="url(#metaGrad)" strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="138" strokeDashoffset="100"
            style={{ animation: "metaSpin 0.9s linear infinite", transformOrigin: "center" }}
          />
          <defs>
            <linearGradient id="metaGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
        </svg>
        {/* Ícone central */}
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <BarChart3 size={18} color="#3b82f6" />
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text1)", marginBottom: 4 }}>Carregando Meta Ads</div>
        <div style={{ fontSize: 12, color: "var(--text2)" }}>Buscando suas campanhas...</div>
      </div>

      <style>{`
        @keyframes metaSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );

  if (!connected) return <ConnectScreen onConnect={() => fetchData()} />;

  const totals      = data?.totals || {};
  const campaigns   = data?.campaigns || [];
  const dailySeries = data?.daily_series || [];

  const handleCampaignUpdate = (campaignId, changes) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        campaigns: prev.campaigns.map((c) =>
          c.id === campaignId ? { ...c, ...changes } : c
        ),
      };
    });
  };

  return (
    <div style={{ padding: "16px", paddingBottom: 100, overflowY: "auto", height: "100%" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.05))",
              border: "1px solid rgba(59,130,246,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 16px rgba(59,130,246,0.2)",
            }}>
              <BarChart3 size={18} color="#3b82f6" />
            </div>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 800, color: "var(--text1)", margin: 0, letterSpacing: "-0.02em" }}>Meta Ads</h1>
              <p style={{ fontSize: 11, color: "var(--text2)", margin: 0, marginTop: 1, fontWeight: 500 }}>Análise com IA das suas campanhas</p>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => { setPixelInput(pixelId); setPageInput(pageId); setShowPixelModal(true); loadMetaOptions(); }}
            title="Configura o Pixel/Dataset ID pra reportar conversões de campanhas clique-para-WhatsApp de volta pra Meta"
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              padding: "8px 10px", borderRadius: 10,
              border: `1px solid ${pixelId ? "rgba(34,197,94,0.35)" : "var(--bg3)"}`,
              background: pixelId ? "rgba(34,197,94,0.06)" : "var(--bg2)",
              cursor: "pointer", transition: "background 0.15s",
            }}
          >
            <Target size={16} color={pixelId ? "#22c55e" : "var(--text2)"} />
            <span style={{ fontSize: 9, fontWeight: 700, color: pixelId ? "#22c55e" : "var(--text2)", whiteSpace: "nowrap" }}>
              {pixelId ? "Pixel ativo" : "Pixel de conversão"}
            </span>
          </button>

          <button
            onClick={handleDisconnect}
            title="Sair da Meta"
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              padding: "8px 10px", borderRadius: 10,
              border: "1px solid rgba(239,68,68,0.3)",
              background: "rgba(239,68,68,0.06)",
              cursor: "pointer", transition: "background 0.15s",
            }}
          >
            <LogOut size={16} color="#ef4444" />
            <span style={{ fontSize: 9, fontWeight: 700, color: "#ef4444", whiteSpace: "nowrap" }}>Sair da Meta</span>
          </button>

          {activeTab === "campanhas" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ display: "flex", background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 10, padding: 3, gap: 2 }}>
              {DATE_RANGES.map(r => (
                <button key={r.value} onClick={() => { setDateRange(r.value); fetchData(r.value); setAnalysis(null); }} style={{
                  padding: "5px 9px", borderRadius: 7, border: "none",
                  background: dateRange === r.value ? "#3b82f6" : "transparent",
                  color: dateRange === r.value ? "#fff" : "var(--text2)",
                  fontSize: 11, fontWeight: 700, cursor: "pointer",
                  boxShadow: dateRange === r.value ? "0 2px 8px rgba(59,130,246,0.35)" : "none",
                  transition: "all 0.2s",
                }}>
                  {r.label}
                </button>
              ))}
            </div>
            <button onClick={() => fetchData()} style={{
              background: "var(--bg2)", border: "1px solid var(--bg3)", borderRadius: 8,
              padding: "6px 8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <RefreshCw size={14} color="var(--text2)" style={{ animation: refreshing ? "spin 0.8s linear infinite" : "none" }} />
            </button>
          </div>
        )}
        </div>
      </div>

      {/* ── Tabs estilo Chrome ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid hsl(var(--border))" }}>
        {[
          { id: "campanhas", label: "Dashboard", icon: BarChart3 },
          { id: "dashboard", label: "Campanhas", icon: Megaphone },
          { id: "pipeline",  label: "Pipeline",  icon: Target },
        ].map(tab => {
          const active = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "11px 20px",
              border: active ? "1px solid hsl(var(--border))" : "1px solid transparent",
              borderBottom: active ? "1px solid var(--bg2)" : "1px solid transparent",
              borderRadius: "12px 12px 0 0",
              background: active ? "var(--bg2)" : "hsl(var(--muted))",
              color: active ? "#3b82f6" : "var(--text1)",
              fontSize: 13.5, fontWeight: active ? 700 : 600, cursor: "pointer",
              opacity: active ? 1 : 0.7,
              marginBottom: -1, transition: "all 0.15s",
            }}>
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Aba Campanhas ── */}
      {activeTab === "campanhas" && (
        <CampaignGoalsView
          campaigns={campaigns}
          dailySeries={dailySeries}
          salesData={salesData}
          goalsData={goalsData}
          onRefresh={() => { fetchData(); fetchSalesAndGoals(); }}
          refreshing={refreshing}
        />
      )}

      {/* ── Aba Pipeline ── */}
      {activeTab === "dashboard" && (
        <DashboardTab
          campaigns={campaigns}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onChangeRange={(r) => { setDateRange(r); fetchData(r); setAnalysis(null); }}
          onOpenDrawer={setDrawerCampaign}
          onCampaignUpdate={handleCampaignUpdate}
          refreshing={refreshing}
        />
      )}

      {activeTab === "pipeline" && <PipelineTab campaigns={campaigns} />}

      {drawerCampaign && (() => {
        const liveCampaign = campaigns.find(c => c.id === drawerCampaign.id) || drawerCampaign;
        return (
          <CampaignDrawer
            campaign={liveCampaign}
            onClose={() => setDrawerCampaign(null)}
            onCampaignUpdate={handleCampaignUpdate}
          />
        );
      })()}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes drawerSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes drawerFade { from { opacity: 0; } to { opacity: 1; } }
        .metaads-grid th:not(:last-child),
        .metaads-grid td:not(:last-child) { border-right: 1px solid hsl(var(--border)); }
        .metaads-grid tbody tr { border-bottom: 1px solid hsl(var(--border)); }
        .metaads-grid thead tr { border-bottom: 1px solid hsl(var(--border)); }
        .metaads-grid thead th { background: hsl(var(--muted)); }
        .utmify-grid th:not(:last-child),
        .utmify-grid td:not(:last-child) { border-right: 1px solid hsl(var(--border)); }
        .utmify-grid tbody tr { border-bottom: 1px solid hsl(var(--border)); }
        .utmify-grid thead tr { border-bottom: 1px solid hsl(var(--border)); }
        .utmify-grid thead th { background: hsl(var(--muted)); }
        .utmify-row:hover { background: var(--bg3) !important; }
        .meta-select option { background: hsl(var(--card)); color: var(--text1); }
        @keyframes aiSpin { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } 100% { transform: rotate(360deg) scale(1); } }
        @keyframes aiPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.4); } 50% { box-shadow: 0 0 0 6px rgba(59,130,246,0); } }
        @keyframes dot1 { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
        @keyframes dot2 { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; animation-delay: 0.15s; } }
        @keyframes dot3 { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; animation-delay: 0.3s; } }
      `}</style>

      {/* Modal do Pixel de conversão (CTWA → Meta Conversions API) */}
      {showPixelModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setShowPixelModal(false)}
        >
          <div
            style={{ background: "var(--bg1, #18181b)", border: "1px solid var(--bg3)", borderRadius: 14, padding: 24, maxWidth: 440, width: "100%", position: "relative" }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPixelModal(false)}
              aria-label="Fechar"
              style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", color: "var(--text2)", cursor: "pointer", padding: 4, lineHeight: 0 }}
            >
              <X size={18} />
            </button>
            <div style={{ fontSize: 30, marginBottom: 10 }}>🎯</div>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: "var(--text1)", marginBottom: 8 }}>
              Pixel de conversão
            </h3>
            <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 14, lineHeight: 1.5 }}>
              Escolha o <strong>Dataset</strong> (Pixel de conversas do WhatsApp) e a <strong>Página</strong>
              vinculada — puxei direto da sua conta Meta, não precisa caçar ID no Gerenciador de Eventos.
              Com isso, toda vez que a Ayla fechar um cliente, a venda é reportada de volta pra Meta —
              funciona tanto pra campanhas de <strong>clique-para-WhatsApp</strong> quanto de <strong>tráfego</strong>.
            </p>

            <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 5, display: "block" }}>
              Dataset / Pixel {loadingOptions && <Loader size={10} className="animate-spin" style={{ display: "inline", marginLeft: 4 }} />}
            </label>
            {datasetOptions.length > 0 ? (
              <select
                className="meta-select"
                value={pixelInput}
                onChange={e => setPixelInput(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg3)", background: "var(--bg2)", color: "var(--text1)", fontSize: 13, marginBottom: 12, boxSizing: "border-box", cursor: "pointer" }}
              >
                <option value="">Selecione...</option>
                {datasetOptions.map(d => <option key={d.id} value={d.id}>{d.name} ({d.id})</option>)}
              </select>
            ) : (
              <input
                type="text"
                value={pixelInput}
                onChange={e => setPixelInput(e.target.value)}
                placeholder={loadingOptions ? "Carregando..." : "Nenhum encontrado — cole o ID manualmente"}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg3)", background: "var(--bg2)", color: "var(--text1)", fontSize: 13, marginBottom: 12, boxSizing: "border-box" }}
              />
            )}

            <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 5, display: "block" }}>
              Página do Facebook (opcional, melhora a atribuição)
            </label>
            {pageOptions.length > 0 ? (
              <select
                className="meta-select"
                value={pageInput}
                onChange={e => setPageInput(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg3)", background: "var(--bg2)", color: "var(--text1)", fontSize: 13, marginBottom: 14, boxSizing: "border-box", cursor: "pointer" }}
              >
                <option value="">Nenhuma</option>
                {pageOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            ) : (
              <input
                type="text"
                value={pageInput}
                onChange={e => setPageInput(e.target.value)}
                placeholder={loadingOptions ? "Carregando..." : "Nenhuma página encontrada (opcional)"}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg3)", background: "var(--bg2)", color: "var(--text1)", fontSize: 13, marginBottom: 14, boxSizing: "border-box" }}
              />
            )}

            <button
              onClick={handleSavePixel}
              disabled={pixelSaving || !pixelInput.trim()}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 9, border: "none",
                background: pixelSaving || !pixelInput.trim() ? "var(--bg3)" : "#22c55e",
                color: pixelSaving || !pixelInput.trim() ? "var(--text2)" : "#000",
                fontSize: 14, fontWeight: 800,
                cursor: pixelSaving || !pixelInput.trim() ? "default" : "pointer",
              }}
            >
              {pixelSaving ? "Salvando..." : "Salvar Pixel"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
